import { useState, useEffect, useCallback } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { ChartLine, MagnifyingGlass, Wallet, ChatCircleText, BellSimple, SignOut, Compass, TrendUp, ChartBar, Target, ListDashes, DeviceMobile, LockSimple, Crown, BookOpen, CurrencyBtc, Sparkle, CurrencyDollar, UserPlus, Clock, X, Robot } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { useSub, TIER_ORDER } from "@/context/SubscriptionContext";
import { Input } from "@/components/ui/input";
import api from "@/lib/api";
import { isNativeApp } from "@/lib/native";

const NAV = [
  { to: "/", label: "Dashboard", icon: Compass, end: true },
  { to: "/stocks", label: "Stocks", icon: ListDashes },
  { to: "/crypto", label: "Crypto", icon: CurrencyBtc },
  { to: "/forex", label: "Forex", icon: CurrencyDollar, need: "pro" },
  { to: "/scanner", label: "Scanner", icon: MagnifyingGlass, need: "pro" },
  { to: "/ideas", label: "AI Trade Ideas", icon: Sparkle, need: "elite" },
  { to: "/backtest", label: "Backtesting", icon: ChartBar, need: "elite" },
  { to: "/auto", label: "Auto-Trader", icon: Robot, need: "elite" },
  { to: "/scorecard", label: "Scorecard", icon: Target, need: "pro" },
  { to: "/portfolio", label: "Paper Trading", icon: Wallet },
  { to: "/assistant", label: "AI Assistant", icon: ChatCircleText },
  { to: "/alerts", label: "Alerts", icon: BellSimple, need: "pro" },
  { to: "/install", label: "Get Mobile App", icon: DeviceMobile },
  { to: "/invite", label: "Invite a Friend", icon: UserPlus },
  { to: "/guide", label: "User Guide", icon: BookOpen },
];

const TABS = [
  { to: "/", label: "Home", icon: Compass, end: true },
  { to: "/stocks", label: "Stocks", icon: ChartBar },
  { to: "/crypto", label: "Crypto", icon: CurrencyBtc },
  { to: "/forex", label: "Forex", icon: CurrencyDollar },
];

function TrialNudge({ onTrial, trial, onView }) {
  const daysLeft = trial?.days_left;
  const storeKey = trial?.ends_at ? `mp_nudge_${new Date(trial.ends_at).toDateString()}` : "mp_nudge";
  const [dismissed, setDismissed] = useState(() => {
    try { return sessionStorage.getItem(storeKey) === "1"; } catch { return false; }
  });
  if (!onTrial || daysLeft == null || daysLeft > 3 || dismissed) return null;
  const endsOn = trial?.ends_at
    ? new Date(trial.ends_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })
    : null;
  const dismiss = () => { try { sessionStorage.setItem(storeKey, "1"); } catch {} setDismissed(true); };
  return (
    <div data-testid="trial-nudge-banner"
      className="flex items-center gap-3 border-b border-signal-yellow/30 bg-signal-yellow/[0.08] px-6 py-2.5">
      <Clock size={16} weight="fill" className="text-signal-yellow shrink-0" />
      <p className="flex-1 text-sm text-foreground">
        <span className="font-medium">Your full access ends {daysLeft <= 0 ? "today" : `in ${daysLeft} day${daysLeft === 1 ? "" : "s"}`}{endsOn ? ` (${endsOn})` : ""}.</span>
        <span className="text-muted-foreground"> Choose a plan to keep Scanner, Forecasts, AI Trade Ideas and live trading.</span>
      </p>
      <button data-testid="trial-nudge-cta" onClick={onView}
        className="shrink-0 inline-flex items-center gap-1.5 rounded-sm bg-signal-yellow/90 hover:bg-signal-yellow text-black px-3 py-1.5 text-xs font-semibold transition-colors duration-200">
        View plans
      </button>
      <button data-testid="trial-nudge-dismiss" onClick={dismiss}
        aria-label="Dismiss" className="shrink-0 text-muted-foreground hover:text-white transition-colors duration-200">
        <X size={16} />
      </button>
    </div>
  );
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { effectiveTier, onTrial, trial } = useSub();
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [liveBroker, setLiveBroker] = useState(false);

  const refreshBroker = useCallback(() => {
    if (TIER_ORDER[effectiveTier] < TIER_ORDER.elite) { setLiveBroker(false); return; }
    api.get("/oanda/status").then((r) => setLiveBroker(!!(r.data?.linked && r.data?.active_env === "live"))).catch(() => setLiveBroker(false));
  }, [effectiveTier]);

  useEffect(() => {
    refreshBroker();
    const id = setInterval(refreshBroker, 30000);
    window.addEventListener("oanda-status-changed", refreshBroker);
    return () => { clearInterval(id); window.removeEventListener("oanda-status-changed", refreshBroker); };
  }, [refreshBroker]);

  const locked = (need) => need && TIER_ORDER[effectiveTier] < TIER_ORDER[need];

  const search = (e) => {
    e.preventDefault();
    const t = q.trim().toUpperCase();
    if (t) { nav(`/stock/${t}`); setQ(""); }
  };

  return (
    <div className="flex h-screen overflow-hidden bg-transparent">
      {liveBroker && (
        <>
          <div data-testid="live-mode-edge" aria-hidden="true"
            className="pointer-events-none fixed inset-0 z-[60] ring-2 ring-inset ring-signal-red/70"
            style={{ boxShadow: "inset 0 0 22px 2px rgba(255,59,48,0.28)" }} />
          <div data-testid="live-mode-banner"
            className="pointer-events-none fixed top-2 left-1/2 -translate-x-1/2 z-[61] flex items-center gap-1.5 rounded-full border border-signal-red/50 bg-signal-red/15 backdrop-blur px-3 py-1 label-xs text-signal-red">
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-signal-red animate-pulse" /> LIVE · REAL MONEY
          </div>
        </>
      )}
      {/* Sidebar */}
      {open && <div className="fixed inset-0 z-40 bg-black/60 lg:hidden" onClick={() => setOpen(false)} />}
      <aside onClick={() => setOpen(false)}
        className={`glass-nav fixed inset-y-0 left-0 z-50 flex w-[220px] flex-col overflow-y-auto border-r border-border transition-transform duration-200 lg:static lg:z-auto lg:translate-x-0 lg:shrink-0 ${open ? "translate-x-0" : "-translate-x-full"}`}>
        <div className="flex items-center gap-2 border-b border-border px-5 min-h-16 box-content" style={{ paddingTop: "env(safe-area-inset-top)" }}>
          <div className="neon-logo flex h-8 w-8 items-center justify-center rounded-lg">
            <TrendUp size={20} weight="bold" className="text-white" />
          </div>
          <div className="leading-tight flex-1">
            <div className="font-heading font-bold text-sm tracking-tight">MarketPilot<span className="glow-text"> AI</span></div>
            <div className="label-xs text-[8px]">Signal · Risk</div>
          </div>
          <button onClick={() => setOpen(false)} data-testid="menu-close" aria-label="Close menu"
            className="lg:hidden p-2 text-muted-foreground hover:text-white"><X size={20} /></button>
        </div>
        <nav className="flex-1 py-3">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} data-testid={`nav-${n.label.toLowerCase().replace(/ /g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-5 py-2.5 text-sm transition-all duration-200 ${
                  isActive ? "nav-active text-white" : "text-muted-foreground hover:text-white hover:bg-white/[0.04] hover:translate-x-0.5"
                }`}>
              <n.icon size={18} />
              <span className="flex-1">{n.label}</span>
              {locked(n.need) && <LockSimple size={13} className="text-muted-foreground/60" />}
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-border p-4">
          <button onClick={() => nav("/account")} data-testid="plan-badge"
            className="w-full flex items-center justify-between mb-3 group">
            {onTrial ? (
              <span data-testid="trial-badge" className="mono text-[10px] uppercase tracking-widest px-2 py-1 rounded-sm border flex items-center gap-1 text-signal-green border-signal-green/40 bg-signal-green/10">
                <Sparkle size={11} weight="fill" /> Trial · {trial?.days_left}d left
              </span>
            ) : (
              <span className={`mono text-[10px] uppercase tracking-widest px-2 py-1 rounded-sm border flex items-center gap-1 ${effectiveTier === "elite" ? "text-signal-yellow border-signal-yellow/40 bg-signal-yellow/10" : effectiveTier === "pro" ? "text-volt border-volt/40 bg-volt/10" : "text-muted-foreground border-border"}`}>
                {effectiveTier === "elite" && <Crown size={11} weight="fill" />} {effectiveTier} plan
              </span>
            )}
            {!isNativeApp() && (onTrial || effectiveTier !== "elite") && <span className="text-[11px] text-volt group-hover:underline">Upgrade</span>}
          </button>
          <div className="mono text-xs text-white truncate">{user?.name}</div>
          <div className="text-[11px] text-muted-foreground truncate">{user?.email}</div>
          <button onClick={logout} data-testid="logout-btn"
            className="mt-3 flex w-full items-center justify-center gap-2 border border-border py-1.5 text-xs text-muted-foreground hover:text-signal-red hover:border-signal-red/40 transition-colors duration-200 rounded-sm">
            <SignOut size={15} /> Sign out
          </button>
          <div className="mt-3 flex flex-wrap items-center gap-x-2 gap-y-1 mono text-[9px] text-muted-foreground/70" data-testid="sidebar-legal">
            <a href="/privacy" target="_blank" rel="noreferrer" className="hover:text-white transition-colors duration-200">Privacy</a>
            <span>·</span>
            <a href="/terms" target="_blank" rel="noreferrer" className="hover:text-white transition-colors duration-200">Terms</a>
            <span>·</span>
            <a href="/risk" target="_blank" rel="noreferrer" className="hover:text-white transition-colors duration-200">Risk</a>
          </div>
        </div>
      </aside>

      {/* Main */}
      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        <TrialNudge onTrial={onTrial && !isNativeApp()} trial={trial} onView={() => nav("/pricing")} />
        <header className="flex min-h-16 box-content shrink-0 items-center justify-between border-b border-border bg-background px-3 sm:px-6 gap-2" style={{ paddingTop: "env(safe-area-inset-top)" }}>
                  <button onClick={() => setOpen(true)} data-testid="menu-btn" aria-label="Open menu"
                    className="lg:hidden shrink-0 p-2 text-muted-foreground hover:text-white">
                    <ListDashes size={22} />
                  </button>
                  <form onSubmit={search} className="relative w-[340px] max-w-[45vw] min-w-0 flex-1 lg:flex-none">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input data-testid="ticker-search" value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Search ticker (e.g. AAPL, NVDA, TSLA)"
              className="pl-9 mono uppercase bg-card border-border h-9 rounded-sm placeholder:normal-case placeholder:text-muted-foreground/70" />
          </form>
          <div className="flex items-center gap-3">
            {onTrial && !isNativeApp() && (
              <button onClick={() => nav("/pricing")} data-testid="trial-banner"
                className="hidden sm:inline-flex items-center gap-1.5 rounded-full border border-signal-green/40 bg-signal-green/10 px-3 py-1 label-xs text-signal-green hover:bg-signal-green/15 transition-colors duration-200">
                <Sparkle size={12} weight="fill" /> Full access · {trial?.days_left} day{trial?.days_left === 1 ? "" : "s"} left
              </button>
            )}
            <div className="hidden md:flex items-center gap-2 label-xs" data-testid="market-mode-indicator">
              {liveBroker ? (
                <><span className="inline-block w-2 h-2 rounded-full bg-signal-red animate-pulse" /> <span className="text-signal-red">Real market data · LIVE trading</span></>
              ) : (
                <><ChartLine size={14} className="text-volt" /> Real market data · Paper trading</>
              )}
            </div>
          </div>
        </header>
        <main id="app-main" className="flex-1 overflow-y-auto p-3 pb-24 sm:p-6 lg:pb-6">{children}</main>
        <nav data-testid="bottom-tabs"
          className="glass-nav fixed bottom-0 inset-x-0 z-30 flex border-t border-border lg:hidden"
          style={{ paddingBottom: "env(safe-area-inset-bottom)" }}>
          {TABS.map((t) => (
            <NavLink key={t.to} to={t.to} end={t.end}
              className={({ isActive }) => `flex flex-1 flex-col items-center gap-0.5 py-2 text-[10px] ${isActive ? "text-volt" : "text-muted-foreground"}`}>
              <t.icon size={22} />{t.label}
            </NavLink>
          ))}
          <button onClick={() => setOpen(true)} data-testid="tab-more"
            className="flex flex-1 flex-col items-center gap-0.5 py-2 text-[10px] text-muted-foreground">
            <ListDashes size={22} />More
          </button>
        </nav>
      </div>
    </div>
  );
}
